import { useCallback, useEffect, useRef, useState } from 'react';
import type { IntlShape } from 'react-intl-next';
import type OriginTracing from '@atlassiansox/origin-tracing';
import { type Template, TemplateType } from '../template/types';
import { AccessRequestCapabilityType } from '../../state/confluence/access-request-capabilities/types';
import { useCreateEmbeddedPage } from '../embedded-page/utils';
import { type BlueprintData } from '../../state/ui/blueprints/types';

const HIDE_DELAY_MS = 250;

export const setSidebarEnabled = (
	areTemplatesEnabled: boolean,
	accessRequestCapability?: AccessRequestCapabilityType,
) => {
	if (areTemplatesEnabled) {
		return areTemplatesEnabled;
	}

	if (
		accessRequestCapability === AccessRequestCapabilityType.DIRECT_ACCESS ||
		accessRequestCapability === AccessRequestCapabilityType.REQUEST_ACCESS
	) {
		return false;
	}
};

interface UseTemplatesPanelControlsWithLicenseParams {
	accessRequestCapability?: AccessRequestCapabilityType;
	areTemplatesEnabled: boolean;
	blueprints: BlueprintData[];
	contentId: string | null;
	intl?: IntlShape;
	origin: OriginTracing | null;
	spaceKey: string | null;
	showError?: () => void;
	projectId?: number;
	projectType?: string;
}

export const useTemplatesPanelControlsWithLicense = ({
	accessRequestCapability,
	areTemplatesEnabled,
	blueprints,
	contentId,
	intl,
	origin,
	spaceKey,
	showError,
	projectId,
	projectType,
}: UseTemplatesPanelControlsWithLicenseParams) => {
	const createEmbeddedPage = useCreateEmbeddedPage({
		spaceKey,
		projectId,
		projectType,
	});
	const [activeTemplate, setActiveTemplate] = useState<Template | undefined>(undefined);
	const hidePreviewTimer = useRef<number | null>(null);

	const cancelHidePreview = useCallback(() => {
		if (hidePreviewTimer.current) {
			clearTimeout(hidePreviewTimer.current);
			hidePreviewTimer.current = null;
		}
	}, []);

	const startHidePreview = useCallback(() => {
		if (hidePreviewTimer.current) {
			return;
		}
		hidePreviewTimer.current = window.setTimeout(() => {
			hidePreviewTimer.current = null;
			setActiveTemplate(undefined);
		}, HIDE_DELAY_MS);
	}, []);

	const showPreview = useCallback(
		(tmpl: Template) => {
			if (tmpl.type !== TemplateType.Confluence) {
				startHidePreview();
				return;
			}
			cancelHidePreview();
			setActiveTemplate(tmpl);
		},
		[cancelHidePreview, startHidePreview],
	);

	const onTemplateClick = (template: Template) => {
		if (
			accessRequestCapability === AccessRequestCapabilityType.REQUEST_ACCESS ||
			accessRequestCapability === AccessRequestCapabilityType.DIRECT_ACCESS ||
			!areTemplatesEnabled
		) {
			return;
		}
		if (template.type === TemplateType.Link) {
			window.open(origin ? origin.addToUrl(template.url) : template.url, '_blank');
			return;
		} else {
			// TemplateType here must be of type Confluence, in which case we bring up the template in an embedded page
			if (!template.blueprintModuleCompleteKey) {
				// create blank with or without prepopulated content
				createEmbeddedPage(
					contentId || undefined,
					true,
					undefined,
					intl && template.title && template.content
						? {
								title: intl.formatMessage(template.title),
								body: template.content,
							}
						: undefined,
					template.key,
				);
				return;
			}
			const blueprint = blueprints.find(
				(blueprint) => blueprint.blueprintModuleCompleteKey === template.blueprintModuleCompleteKey,
			);
			if (!blueprint) {
				// site probably has this template turned off, we need to show an error
				showError && showError();
				return;
			}
			createEmbeddedPage(contentId || undefined, true, blueprint);
		}
	};
	const sidebarEnabled = setSidebarEnabled(areTemplatesEnabled, accessRequestCapability);
	// on unmount, clear any pending timers to prevent operations on unmounted components
	useEffect(
		() => () => {
			if (hidePreviewTimer.current) {
				clearTimeout(hidePreviewTimer.current);
			}
		},
		[],
	);

	return {
		props: {
			onTemplateClick,
			onMouseLeave: startHidePreview,
			onTemplateMouseEnter: showPreview,
			isDisabled: !sidebarEnabled,
		},
		hoverPreviewProps: {
			onMouseEnter: cancelHidePreview,
			onMouseLeave: startHidePreview,
			template: activeTemplate,
		},
	};
};
